import React, { ComponentProps } from "react";

import AppContextValueProvider from "../../../App/Compositions/AppContextValueProvider";
import UserEventContextValueProvider from "../../../App/Compositions/UserEventContextValueProvider";
import NoConfirmationNewPatientFlow from "../../../Experiment/Types/NoConfirmationNewPatientFlow";
import NoConfirmationNoBookFlow from "../../../Experiment/Types/NoConfirmationNoBookFlow";
import NoConfirmationRebookFlow from "../../../Experiment/Types/NoConfirmationRebookFlow";
import FlowStepOutletValueProvider from "../../../Routing/Composition/FlowStepOutletValueProvider";
import { IFlowStepProps } from "../../../Types/IFlow";
import ServiceSelector from "../../CenterSelect/Components/ServiceSelector";
import { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import AppContextReservationCounter from "../../Compositions/AppContextReservationCounter";
import IUserEventSource from "../../Types/IUserEventSource";
import SchedulingStepWrapper, {
  ISchedulingStepWrapperConfig,
  ISchedulingStepWrapperProps
} from "../Components/SchedulingStepWrapper";
import SwitchingSchedulingStep, { ISchedulingStepResolver } from "../Components/SwitchingSchedulingStep";
import AppContextSchedulingConfirmStepWrapper from "./AppContextSchedulingConfirmStepWrapper";
import AppContextSchedulingLoader from "./AppContextSchedulingLoader";
import AppContextSchedulingStep from "./AppContextSchedulingStep";
import AppContextUseCreateReservation from "./AppContextUseCreateReservation";
import AppContextUseReleaseReservation from "./AppContextUseReleaseReservation";
import DynamicScheduleSlotSelector from "./DynamicScheduleSlotSelector";

const RoutedSchedulingStepWrapper = FlowStepOutletValueProvider({
  Component: UserEventContextValueProvider({ Component: SchedulingStepWrapper })
});

const AppContextSchedulingStepWrapper: React.FC<ISchedulingStepWrapperConfig> = AppContextValueProvider<
  Omit<ISchedulingStepWrapperProps, keyof IFlowStepProps | keyof IUserEventSource>,
  ComponentProps<typeof RoutedSchedulingStepWrapper>
>({
  Component: RoutedSchedulingStepWrapper,
  map: ({ bookingController, customerCState, reservation, router, setReservation }) => {
    if (!bookingController) throw new Error("Booking controller is required for scheduling step.");
    return {
      value: reservation?.slot ?? null,
      onChange: setReservation,
      customerFirstName: customerCState.firstName,
      allCentersUnbookable: !bookingController.isBookingPossible,
      router
    };
  }
});

const SchedulingStep: ISchedulingStepWrapperConfig["Scheduler"] = props => (
  <AppContextSchedulingStep
    {...props}
    useReleaseReservation={AppContextUseReleaseReservation}
    ServiceSelector={ServiceSelector}
    SlotSelector={DynamicScheduleSlotSelector}
    ReservationCounter={AppContextReservationCounter}
  />
);

const StandardSchedulingStep: React.FC<IStepWrapperButtonProps> = props => (
  <AppContextSchedulingStepWrapper
    {...props}
    useCreateReservation={AppContextUseCreateReservation}
    Scheduler={SchedulingStep}
  />
);

const noConfirmationFlowNames = new Set([
  NoConfirmationNewPatientFlow.flowName,
  NoConfirmationRebookFlow.flowName,
  NoConfirmationNoBookFlow.flowName
]);

const schedulingStepDelegates: ISchedulingStepResolver[] = [
  { isMatch: flow => noConfirmationFlowNames.has(flow.name), SchedulingStep: AppContextSchedulingConfirmStepWrapper },
  { isMatch: () => true, SchedulingStep: StandardSchedulingStep }
];

const HybridCalendarSchedulingStep: React.FC<IStepWrapperButtonProps> = props => (
  <AppContextSchedulingLoader>
    <SwitchingSchedulingStep {...props} delegateSteps={schedulingStepDelegates} />
  </AppContextSchedulingLoader>
);

export default HybridCalendarSchedulingStep;
